/**
 * UserAccountManagement Component
 *
 * User account profile and settings management.
 * Refactored into modular structure with extracted modal components.
 * Individual cards for each profile element with improved UX and clear action hierarchy.
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';

// 2. Internal absolute imports
import { Card, CardBody, useToastNotification } from '@enfomentis/ui';
import { useAuth } from '@/hooks/useAuth';

// 3. Relative imports
import { ProfileInfoCard } from './ProfileInfoCard';
import { AccountInfoCard } from './AccountInfoCard';
import { SubscriptionInfoCard } from './SubscriptionInfoCard';
import { SecurityCard } from './SecurityCard';

/**
 * User Account Management Component
 *
 * Redesigned with modular structure:
 * - ProfileInfoCard: Display name and email management
 * - AccountInfoCard: Password, account info, license, development status
 * - Extracted modals for reusability across user and admin apps
 * - Mobile-responsive vertical layout
 * - Scalable structure for future features
 */
export const UserAccountManagement: React.FC = () => {
  const { user, getPendingEmailChange, cancelEmailChange, resendEmailVerification } = useAuth();
  const toast = useToastNotification();

  // Pending email change state
  const [pendingEmailChange, setPendingEmailChange] = useState<{ newEmail: string; createdAt: string } | null>(null);

  // Check for pending email changes
  const checkPendingEmailChange = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      const pending = await getPendingEmailChange();
      setPendingEmailChange(pending);
    } catch (error) {
      console.error('Failed to check pending email change:', error);
      setPendingEmailChange(null);
    }
  }, [user, getPendingEmailChange]);

  // Check for pending email changes on component mount
  React.useEffect(() => {
    checkPendingEmailChange();
  }, [checkPendingEmailChange]);

  // Handle resend email verification
  const handleResendEmailVerification = useCallback(async () => {
    try {
      const result = await resendEmailVerification();
      if (result.success) {
        toast.showToast({
          title: 'Verification email sent',
          description: result.message,
          color: 'success',
          timeout: 4000
        });
      } else {
        toast.showToast({
          title: 'Failed to resend email',
          description: result.message,
          color: 'danger',
          timeout: 4000
        });
      }
    } catch (_error) {
      toast.showToast({
        title: 'Error',
        description: 'Failed to resend verification email',
        color: 'danger',
        timeout: 4000
      });
    }
  }, [resendEmailVerification, toast]);

  // Handle cancel email change
  const handleCancelEmailChange = useCallback(async () => {
    try {
      const result = await cancelEmailChange();
      if (result.success) {
        setPendingEmailChange(null);
        toast.showToast({
          title: 'Email change cancelled',
          description: result.message,
          color: 'success',
          timeout: 4000
        });
      } else {
        toast.showToast({
          title: 'Failed to cancel',
          description: result.message,
          color: 'danger',
          timeout: 4000
        });
      }
    } catch (_error) {
      toast.showToast({
        title: 'Error',
        description: 'Failed to cancel email change',
        color: 'danger',
        timeout: 4000
      });
    }
  }, [cancelEmailChange, toast]);

  // Handle email change success (refresh pending email state)
  const handleEmailChangeSuccess = useCallback(() => {
    checkPendingEmailChange();
  }, [checkPendingEmailChange]);

  if (!user) {
    return (
      <div className="max-w-4xl space-y-6">
        <Card>
          <CardBody className="p-6 text-center">
            <p className="text-text-secondary">
              Please sign in to view your account information.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Profile Cards */}
      <div className="space-y-4">
        <ProfileInfoCard
          user={user}
          pendingEmailChange={pendingEmailChange}
          onResendEmailVerification={handleResendEmailVerification}
          onCancelEmailChange={handleCancelEmailChange}
          onEmailChangeSuccess={handleEmailChangeSuccess}
        />

        <AccountInfoCard user={user} />

        <SubscriptionInfoCard />

        <SecurityCard />
      </div>
    </div>
  );
};
